import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ICallFlowStep } from '@dataTypes/clinic.type'
import { Checkbox, InputField, Select } from '@enamelai/design-system'
import {
  REROUTE_CALL_IF_NO_ANSWER_OPTIONS,
  REROUTE_CALL_RING_TYPE_OPTIONS,
  REROUTE_CALL_YES_NO_OPTIONS,
} from '@constants/Settings'
import StepPanelShell from './StepPanelContainer.component'

export type RerouteCallPanelProps = {
  step:
    | Extract<ICallFlowStep, { type: 'route_call' }>
    | Extract<ICallFlowStep, { type: 'reroute_call' }>
  branchLabel: string
  aiEnabled?: boolean
  onChange: (
    patch: Partial<
      | Extract<ICallFlowStep, { type: 'route_call' }>
      | Extract<ICallFlowStep, { type: 'reroute_call' }>
    >,
  ) => void
  onCancel: () => void
  onDone: () => void
}

const RerouteCallPanel = ({
  step,
  branchLabel,
  aiEnabled = false,
  onChange,
  onCancel,
  onDone,
}: RerouteCallPanelProps): React.ReactElement => {
  const { teams } = useTeamCatalog()
  const [validationMessages, setValidationMessages] = useState<
    Record<string, string>
  >({})
  const noAnswerOptions = useMemo(
    () =>
      REROUTE_CALL_IF_NO_ANSWER_OPTIONS.filter(
        (option) => option.value !== 'ai_receptionist' || aiEnabled,
      ),
    [aiEnabled],
  )

  const clearValidation = useCallback(() => {
    setValidationMessages({})
  }, [])

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (
      step.voicemailAllowRerecord === true &&
      step.includeVoicemailTranscription === undefined
    ) {
      onChangeRef.current({ includeVoicemailTranscription: false })
    }
  }, [step.voicemailAllowRerecord, step.includeVoicemailTranscription])

  const rerecordValue =
    step.voicemailAllowRerecord == null
      ? null
      : step.voicemailAllowRerecord
        ? 'yes'
        : 'no'

  const rerecordYes = step.voicemailAllowRerecord === true
  const isVoicemailNoAnswer = step.ifNoAnswer === 'voicemail'
  const isTtsNoAnswer = step.ifNoAnswer === 'tts'

  const handleDone = (): void => {
    const errs: Record<string, string> = {}
    if (!step.team?.trim()) {
      errs.team = 'Select a team.'
    }
    if (!step.ringType?.trim()) {
      errs.ringType = 'Select ring type.'
    }
    if (
      step.ringDurationSec == null ||
      Number.isNaN(step.ringDurationSec) ||
      step.ringDurationSec <= 0
    ) {
      errs.ringDurationSec = 'Invalid seconds.'
    }
    if (!step.ifNoAnswer?.trim()) {
      errs.ifNoAnswer = 'Select an action.'
    }
    if (step.ifNoAnswer === 'voicemail') {
      if (step.voicemailAllowRerecord == null) {
        errs.voicemailAllowRerecord = 'Select yes or no.'
      }
      if (step.voicemailAllowRerecord === true) {
        if (
          step.voicemailMaxRecordingSec == null ||
          Number.isNaN(step.voicemailMaxRecordingSec) ||
          step.voicemailMaxRecordingSec <= 0
        ) {
          errs.voicemailMaxRecordingSec = 'Invalid seconds.'
        }
        if (step.includeVoicemailTranscription == null) {
          errs.includeVoicemailTranscription =
            'Select whether to include transcription.'
        }
      }
    }
    if (step.ifNoAnswer === 'tts' && !step.ifNoAnswerTtsText?.trim()) {
      errs.ifNoAnswerTtsText = 'Enter a text to speech message.'
    }
    if (Object.keys(errs).length > 0) {
      setValidationMessages(errs)
      return
    }
    setValidationMessages({})
    onDone()
  }

  return (
    <StepPanelShell
      title="Route call"
      branchLabel={branchLabel}
      onCancel={onCancel}
      onDone={handleDone}
    >
      <div className="scp-panel-field scp-route-call">
        <Select
          label="Team"
          placeholder="Select team"
          options={teams.map((team) => ({
            label: team,
            value: team,
          }))}
          value={step.team || undefined}
          error={!!validationMessages.team}
          helperText={validationMessages.team}
          onChange={(value) => {
            clearValidation()
            const next = String(value || '')
            onChange({ team: next || undefined })
          }}
        />
      </div>
      <div className="scp-row">
        <Select
          label="Ring type"
          placeholder="Select ring type"
          options={REROUTE_CALL_RING_TYPE_OPTIONS}
          value={step.ringType || undefined}
          error={!!validationMessages.ringType}
          helperText={validationMessages.ringType}
          onChange={(value) => {
            clearValidation()
            const next = String(value || '')
            onChange({ ringType: next || undefined })
          }}
        />
        <InputField
          className="scp-ring-duration-input"
          variant="number"
          label="Ring duration"
          numberMin={1}
          placeholder="20"
          clearable={false}
          trailingIcon={<span>sec</span>}
          value={
            step.ringDurationSec != null && !Number.isNaN(step.ringDurationSec)
              ? String(step.ringDurationSec)
              : ''
          }
          onChange={(event) => {
            clearValidation()
            const raw = event.target.value
            onChange({
              ringDurationSec: raw === '' ? undefined : Math.floor(Number(raw)),
            })
          }}
          error={!!validationMessages.ringDurationSec}
          helperText={validationMessages.ringDurationSec}
        />
      </div>

      <div className="scp-panel-field">
        <Select
          label="If no answer"
          placeholder="Select action"
          options={noAnswerOptions}
          value={step.ifNoAnswer || undefined}
          error={!!validationMessages.ifNoAnswer}
          helperText={validationMessages.ifNoAnswer}
          onChange={(value) => {
            clearValidation()
            const next = String(value || '') || undefined
            if (next === 'voicemail') {
              onChange({
                ifNoAnswer: next,
                ifNoAnswerTtsText: undefined,
              })
              return
            }
            if (next === 'tts') {
              onChange({
                ifNoAnswer: next,
                voicemailAllowRerecord: undefined,
                voicemailMaxRecordingSec: undefined,
                includeVoicemailTranscription: undefined,
              })
              return
            }
            onChange({
              ifNoAnswer: next,
              ifNoAnswerTtsText: undefined,
              voicemailAllowRerecord: undefined,
              voicemailMaxRecordingSec: undefined,
              includeVoicemailTranscription: undefined,
            })
          }}
        />
      </div>

      {isTtsNoAnswer && (
        <div className="scp-panel-field">
          <InputField
            variant="message"
            fullWidth
            showCounter={false}
            label="No answer text to speech"
            value={step.ifNoAnswerTtsText ?? ''}
            placeholder="Enter message to play when no one answers."
            onChange={(e) => {
              clearValidation()
              onChange({ ifNoAnswerTtsText: e.target.value })
            }}
            error={!!validationMessages.ifNoAnswerTtsText}
            helperText={validationMessages.ifNoAnswerTtsText}
          />
        </div>
      )}

      {isVoicemailNoAnswer && (
        <div className="scp-row">
          <div className="scp-panel-field">
            <Select
              label="Allow caller to re-record"
              placeholder="Select"
              options={REROUTE_CALL_YES_NO_OPTIONS}
              value={rerecordValue || undefined}
              error={!!validationMessages.voicemailAllowRerecord}
              helperText={validationMessages.voicemailAllowRerecord}
              onChange={(value) => {
                clearValidation()
                const next = String(value || '')
                if (next === 'yes') {
                  onChange({
                    voicemailAllowRerecord: true,
                    includeVoicemailTranscription:
                      step.includeVoicemailTranscription ?? false,
                  })
                } else if (next === 'no') {
                  onChange({
                    voicemailAllowRerecord: false,
                    voicemailMaxRecordingSec: undefined,
                    includeVoicemailTranscription: undefined,
                  })
                } else {
                  onChange({
                    voicemailAllowRerecord: undefined,
                    voicemailMaxRecordingSec: undefined,
                    includeVoicemailTranscription: undefined,
                  })
                }
              }}
            />
          </div>
          <div className="scp-panel-field scp-recording-length">
            <InputField
              variant="number"
              fullWidth
              label="Max recording length"
              numberMin={1}
              placeholder="160"
              disabled={!rerecordYes}
              clearable={false}
              trailingIcon={<span>sec</span>}
              value={
                step.voicemailMaxRecordingSec != null &&
                !Number.isNaN(step.voicemailMaxRecordingSec)
                  ? String(step.voicemailMaxRecordingSec)
                  : ''
              }
              onChange={(event) => {
                clearValidation()
                const raw = event.target.value
                onChange({
                  voicemailMaxRecordingSec:
                    raw === '' ? undefined : Math.floor(Number(raw)),
                })
              }}
              error={!!validationMessages.voicemailMaxRecordingSec}
              helperText={validationMessages.voicemailMaxRecordingSec}
            />
          </div>
        </div>
      )}

      {isVoicemailNoAnswer && (
        <div className="scp-panel-field">
          <div
            className={`scp-switch-label${!rerecordYes ? ' scp-switch-label--disabled' : ''}`}
          >
            <Checkbox
              checked={!!step.includeVoicemailTranscription}
              disabled={!rerecordYes}
              onChange={(event) => {
                clearValidation()
                onChange({
                  includeVoicemailTranscription: event.target.checked,
                })
              }}
              label="Include transcription"
            />
          </div>
        </div>
      )}
    </StepPanelShell>
  )
}

export default withTeamCatalog(RerouteCallPanel)
